import { useState, useCallback, useRef, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { GalleryData, ImgUrl, VidUrl } from "../Components/common/types";
// import { log } from "../utils/logger";
import { fileSignature } from "../utils/helperFunctions";
import { log } from "../utils/logger";

export const useFileManager = (initialGalleryData: GalleryData) => {
  const [galleryData, setGalleryData] =
    useState<GalleryData>(initialGalleryData);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const dragState = useRef<{
    index: number | null;
    type: "image" | "video" | null;
    node: EventTarget | null;
  }>({ index: null, type: null, node: null });
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);
  const touchCurrentItem = useRef<number | null>(null);

  const [editingItem, setEditingItem] = useState<{
    id: string;
    isImage: boolean;
  } | null>(null);

  const existingFileSignatureRef = useRef<Set<string>>(new Set());
  const fileCacheRef = useRef<Set<string>>(new Set());

  //Get file signatures from existing files in db
  useEffect(() => {
    if (initialGalleryData) {
      const existingSignatures = new Set<string>();

      initialGalleryData.imgData.forEach((img) => {
        if (img.fileSignature) {
          existingSignatures.add(img.fileSignature);
        }
      });

      initialGalleryData.vidData.forEach((vid) => {
        if (vid.fileSignature) {
          existingSignatures.add(vid.fileSignature);
        }
      });

      existingFileSignatureRef.current = existingSignatures;
    }
  }, [initialGalleryData]);

  // ===== FILE MANAGEMENT ===== Add files (from input or drop)
  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const newFiles: File[] = [];
      const images: ImgUrl[] = [];
      const videos: VidUrl[] = [];
      let hasDuplicates = false;

      Array.from(files).forEach((file) => {
        //Create file signature for each file
        const sig = fileSignature(file);

        //Check duplicates here, compare size, name
        const isDuplicateInSession = fileCacheRef.current.has(sig);; //check if exact same file is selected
        const isDuplicateInDb = existingFileSignatureRef.current.has(sig); //check if file selected exist in

        if (isDuplicateInSession || isDuplicateInDb) {
          hasDuplicates = true;
          return;
        }

         fileCacheRef.current.add(sig); // Add to session cache

        //Past here we have only unique files
        newFiles.push(file);

        //preparing file data
        const id = uuidv4();
        const tempUrl = URL.createObjectURL(file);
        const name = file.name;

        if (file.type.startsWith("image/")) {
          images.push({
            id,
            mimetype: file.type,
            alt: name,
            src: tempUrl,
            description: "",//filled when editor is opened
            fileSignature: sig,
            publicId: "", // to be filled post-upload
            file,
            isCover: false, // default false
            
          });
        } else if (file.type.startsWith("video/")) {
          videos.push({
            id,
            mimetype: file.type,
            src: tempUrl,
            description: "",//filled when editor is opened
            fileName: name,
            fileSignature: sig,
            publicId: "",
            file,// to be filled post-upload
            isCover: false,
         
          });
        }
      });

      const updatedFiles = [...selectedFiles, ...newFiles]; //selected files update
      const updatedGalleryData = {
        imgData: [...galleryData.imgData, ...images],
        vidData: [...galleryData.vidData, ...videos],
      }; //gallary data update

      //update both states, selected files and galleryData
      setSelectedFiles(updatedFiles);
      setGalleryData(updatedGalleryData);

      return {
        updatedGalleryData,
        hasDuplicates,
        newFilesCount: newFiles.length,
      };
    },
    [selectedFiles, galleryData]
  );

  // ===== FILE UPLOAD (DROPZONE) =====
  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles]
  );

  // ===== ITEM REORDERING =====
  // Mouse events
  const handleDragStart = useCallback(
    (e: React.DragEvent, index: number, isImage: boolean) => {
      dragState.current = {
        index,
        type: isImage ? "image" : "video",
        node: e.target,
      };

      (e.target as HTMLElement).style.opacity = "0.5";

      // Set drag image (optional)
      if (e.dataTransfer) {
        const img = new Image();
        img.src =
          "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
        e.dataTransfer.setDragImage(img, 0, 0);
        e.dataTransfer.setData("text/plain", ""); // Required for Firefox
        e.dataTransfer.effectAllowed = "move";
      }
    },
    []
  );

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    if (dragState.current.node) {
      const node = dragState.current.node as HTMLElement;
      node.style.opacity = "1";
      node.style.transform = ""; // Reset any inline transforms
    }
    dragState.current = { index: null, type: null, node: null };
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number, isImage: boolean) => {
      e.preventDefault();
      // Skip if no item is being dragged or if hovering over same item
      if (
        dragState.current.index === null ||
        dragState.current.index === index ||
        dragState.current.type !== (isImage ? "image" : "video")
      ) {
        return;
      }

      setGalleryData((prev) => {
        if (isImage) {
          const newImgData = [...prev.imgData];
          const [draggedItem] = newImgData.splice(dragState.current.index!, 1);
          newImgData.splice(index, 0, draggedItem);
          return { ...prev, imgData: newImgData };
        } else {
          const newVidData = [...prev.vidData];
          const [draggedItem] = newVidData.splice(dragState.current.index!, 1);
          newVidData.splice(index, 0, draggedItem);
          return { ...prev, vidData: newVidData };
        }
      });

      // Update the drag index but keep the same type
      dragState.current = {
        ...dragState.current,
        index: index,
      };
    },
    []
  );

  // Touch events
  const handleTouchStart = useCallback((e: React.TouchEvent, index: number) => {
    touchStartPos.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    touchCurrentItem.current = index;
    (e.currentTarget as HTMLElement).style.opacity = "0.5";
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent, index: number, isImage: boolean) => {
      if (!touchStartPos.current || dragState.current.index === null) return;

      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartPos.current.x);
      const deltaY = Math.abs(touch.clientY - touchStartPos.current.y);

      // Only activate drag if movement threshold is crossed
      if (deltaX > 10 || deltaY > 10) {
        const dragEvent = {
          ...e,
          preventDefault: () => {},
          dataTransfer: { effectAllowed: "move" },
        } as unknown as React.DragEvent;

        handleDragOver(dragEvent, index, isImage);
        dragState.current = {
          ...dragState.current,
          index: index,
        };
      }
    },
    [handleDragOver]
  );

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (e.currentTarget) {
      (e.currentTarget as HTMLElement).style.opacity = "1";
    }
    touchStartPos.current = null;
    touchCurrentItem.current = null;
  }, []);

  // ===== OTHER FILE OPERATIONS =====
  const removeFile = useCallback(
    (id: string, isImage: boolean) => {
      const updatedGalleryData: GalleryData = {
        imgData: isImage
          ? galleryData.imgData.filter((img) => img.id !== id)
          : galleryData.imgData,
        vidData: !isImage
          ? galleryData.vidData.filter((vid) => vid.id !== id)
          : galleryData.vidData,
      };
      setGalleryData(updatedGalleryData);

      //Update select files too
      setSelectedFiles((prev) =>
        prev.filter((file) => {
          if (isImage) {
            return !galleryData.imgData.some(
              (img) => img.id === id && img.alt === file.name
            );
          } else {
            return !galleryData.vidData.some(
              (vid) => vid.id === id && vid.fileName === file.name
            );
          }
        })
      );

      return { updatedGalleryData };
    },
    [galleryData]
  );

  const updateDescription = useCallback(
    (id: string, field: "description", value: string, isImage: boolean) => {
      setGalleryData((prev) => ({
        ...prev,
        [isImage ? "imgData" : "vidData"]: prev[
          isImage ? "imgData" : "vidData"
        ].map((item) => (item.id === id ? { ...item, [field]: value } : item)),
      }));
    },
    []
  );

  const openEditor = (id: string, isImage: boolean) => {
    setEditingItem({ id, isImage });
  };

  const closeEditor = useCallback(() => {
    setEditingItem(null);
  }, []);

  //Clear blobs url
 useEffect(() => {
  log("Executing this part, clearing blobs")
  return () => {
    galleryData.imgData.forEach((img) => URL.revokeObjectURL(img.src));
    galleryData.vidData.forEach((vid) => URL.revokeObjectURL(vid.src));
  };
}, [galleryData]);

  return {
    galleryData,
    selectedFiles,
    isDragging,
    editingItem,
    dragState: dragState.current,
    setGalleryData,
    setSelectedFiles,
    openEditor,
    closeEditor,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    addFiles,
    removeFile,
    updateDescription,
  };
};
